import { Button, MenuItem } from '@lokalise/louis'
import type { RagProfileConfig, Status } from '@lokalise/translation-api-schemas'
import { useNavigate } from 'react-router-dom'
import { AI_PROFILES_DOCUMENTATION_URL } from '../../../constants/url'
import { useDeactivateProfile } from '../../../hooks/useDeactivateProfile'
import { getNewEvaluationsUrl } from '../../../utils/url'
import { CardContainer } from '../components/CardContainer/CardContainer'
import { CardModel } from '../components/CardModel/CardModel'
import { CardTitle } from '../components/CardTitle/CardTitle'
import { EditMenu } from '../components/EditMenu/EditMenu'
import { EvaluationsButton } from '../components/EvaluationsButton/EvaluationsButton'
import { ProjectsList } from '../components/ProjectsList/ProjectsList'
import { CustomTag, ProjectTag } from '../components/ProjectTag/ProjectTag'
import styles from './ProfileCardTM.module.css'

type ProfileCardTMProps = {
  title: string
  status: Status
  profile: RagProfileConfig
  hasEditPermissions?: boolean
  hasTMEvaluationEnabled?: boolean
  onOpenAssignmentModal: () => void
}

export const ProfileCardTM = ({
  status,
  title,
  profile,
  hasEditPermissions,
  hasTMEvaluationEnabled,
  onOpenAssignmentModal,
}: ProfileCardTMProps) => {
  const navigate = useNavigate()
  const { deactivate, isPending: isDeactivating } = useDeactivateProfile({ profile })

  const hasEvaluationReport = (profile?.evaluations?.count || 0) > 0
  const isEvaluationInProgress = profile.evaluations?.status === 'in-progress'
  const newEvaluationsPageUrl = getNewEvaluationsUrl(profile.id)
  const hasAssignedProfile = profile.projects.length > 0

  const isActive = status === 'active'

  const cardText =
    'The AI profile leverages Translation Memories (TMs) assigned in each project to improve translation quality—often delivering better results than the base AI profile. It updates automatically whenever TMs are modified. '

  const showActivateToUse = hasEditPermissions && !hasAssignedProfile
  const showEditMenu = hasEditPermissions && hasAssignedProfile
  const showEvaluations = hasEditPermissions && hasTMEvaluationEnabled && hasAssignedProfile

  return (
    <CardContainer>
      <div className="flex justify-between" data-testid="profile-card-tm">
        <div className="flex-col gap-3">
          <div className="flex gap-2">
            <CustomTag />
            <ProjectTag type={isDeactivating ? 'updating' : profile.status} />
            <CardModel integration={profile.config.integration} />
          </div>
          <CardTitle title={title} tooltip={isActive ? cardText : ''} />
        </div>
        <div className={`${styles.editMenuContainer} flex gap-2 items-center`}>
          {showEvaluations && (
            <EvaluationsButton
              profileId={profile.id}
              isInProgress={isEvaluationInProgress}
              hasEvaluations={hasEvaluationReport}
            />
          )}
          {showEditMenu && (
            <EditMenu>
              <MenuItem onClick={onOpenAssignmentModal}>Edit</MenuItem>
              {hasEvaluationReport && showEvaluations && (
                <MenuItem
                  onClick={() => navigate(newEvaluationsPageUrl)}
                  disabled={isEvaluationInProgress}
                >
                  Reevaluate
                </MenuItem>
              )}
              <MenuItem onClick={() => deactivate({ profileId: profile.id })}>Deactivate</MenuItem>
            </EditMenu>
          )}
        </div>
        {showActivateToUse && (
          <Button type="button" variant="secondary" size="sm" onClick={onOpenAssignmentModal}>
            Activate to use
          </Button>
        )}
      </div>
      {isActive && hasAssignedProfile ? (
        <ProjectsList projects={profile.projects} />
      ) : (
        <div className={styles.bodyText}>
          <span>
            {cardText}{' '}
            <a href={AI_PROFILES_DOCUMENTATION_URL} rel="noreferrer" target="_blank">
              Learn more
            </a>
          </span>
        </div>
      )}
    </CardContainer>
  )
}
